import React from "react";

import PageRenderer from "@agent-ui/features/page-renderer/components/PageRenderer";
import { useViewportStore } from "@agent-ui/features/page-renderer/store/viewportStore";
import twMerge from "@replo/design-system/utils/twMerge";
import { DesktopPreview } from "@v2/components/DesktopPreview";
import { MobilePreview } from "@v2/components/MobilePreview";
import { CANVAS_SIZES } from "@v2/utils/canvas-size";
import useMeasure from "react-use-measure";

type DevicePreviewProps = React.PropsWithChildren<{
  pageRoute: string;
  className?: string;
  targetViewport?: "mobile" | "desktop";
  isScrollable?: boolean;
  hideScrollbars?: boolean;
  isInteractive?: boolean;
  // Note (Ezra, Dec 3 2025): URL to display in the desktop browser mockup's URL bar.
  // Typically the published page URL or shop domain as fallback.
  url?: string;
  mobileClassName?: string;
  desktopClassName?: string;
  maxMobileWidth?: number;
  rendererContainerRef?: (node: HTMLDivElement | null) => void;
  cropToFit?: boolean;
  /**
   * NOTE (Yuxin, 2026-01-31): When provided, renders an iframe with this URL
   * directly instead of using PageRenderer. Use this for sandbox previews
   * where we want to show the live sandbox content, not cached HTML.
   */
  iframeSrc?: string;
  /** Fires when the direct iframe (iframeSrc) finishes loading its content. */
  onIframeLoad?: () => void;
}>;

export const DevicePreview: React.FC<DevicePreviewProps> = ({
  pageRoute,
  className,
  targetViewport,
  isScrollable = false,
  hideScrollbars,
  isInteractive = false,
  url,
  mobileClassName,
  desktopClassName,
  children,
  maxMobileWidth,
  rendererContainerRef,
  cropToFit = false,
  iframeSrc,
  onIframeLoad,
}) => {
  const { currentViewport } = useViewportStore();
  const viewport = targetViewport ?? currentViewport;
  const [containerRef, containerBounds] = useMeasure({ offsetSize: true });

  const desktopFrameScale =
    containerBounds.width > 0
      ? containerBounds.width / CANVAS_SIZES.desktop.defaultFrameWidth
      : 1;

  // NOTE (Yuxin, 2026-01-31): Calculate scale for desktop iframe content.
  // We render the iframe at full viewport width and scale it down to fit the container.
  const desktopViewportWidth = CANVAS_SIZES.desktop.defaultFrameWidth;
  const desktopScale =
    containerBounds.width > 0
      ? containerBounds.width / desktopViewportWidth
      : 1;

  // NOTE (Yuxin, 2026-01-31): When iframeSrc is provided, render an iframe directly
  // instead of PageRenderer. The iframe is rendered at full viewport size and scaled down.
  // NOTE (Berkley, 2026-02-06): For non-interactive previews (thumbnails), omit allow-scripts
  // so the SSR HTML renders without client-side hydration. Next.js hydration can fail in
  // sandboxed iframes causing "Application error", but the server-rendered HTML is sufficient
  // for a visual thumbnail.
  const iframeSandbox = isInteractive
    ? "allow-scripts allow-same-origin"
    : "allow-same-origin";

  // NOTE (Berkley, 2026-02-06): For mobile, render the iframe without any scaling
  // wrapper. MobilePreview already scales the entire device to fit the container,
  // so pre-scaling the iframe causes double-scaling (content appears tiny in the
  // upper-left corner). The device screen from devices.css is 390px wide, which is
  // the correct mobile viewport width for the iframe content.
  const previewContent = iframeSrc ? (
    <iframe
      src={iframeSrc}
      className={twMerge("h-full w-full border-0", className)}
      sandbox={iframeSandbox}
      scrolling={isScrollable ? "auto" : "no"}
      onLoad={onIframeLoad}
    />
  ) : (
    children ?? (
      <PageRenderer
        pageRoute={pageRoute}
        className={className}
        isScrollable={isScrollable}
        hideScrollbars={hideScrollbars}
        isInteractive={isInteractive}
        containerRef={rendererContainerRef}
      />
    )
  );

  const desktopPreviewContent = iframeSrc ? (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ overflow: "hidden" }}
    >
      <div
        style={{
          transformOrigin: "top left",
          transform: `scale(${desktopScale})`,
          width: `${desktopViewportWidth}px`,
          height: `${containerBounds.height > 0 ? containerBounds.height / desktopScale : 900}px`,
        }}
      >
        <iframe
          src={iframeSrc}
          className={twMerge("h-full w-full border-0", className)}
          sandbox={iframeSandbox}
          scrolling={isScrollable ? "auto" : "no"}
          onLoad={onIframeLoad}
        />
      </div>
    </div>
  ) : (
    children ?? (
      <PageRenderer
        pageRoute={pageRoute}
        className={className}
        targetViewport="desktop"
        isScrollable={isScrollable}
        hideScrollbars={hideScrollbars}
        isInteractive={isInteractive}
        containerRef={rendererContainerRef}
      />
    )
  );

  return (
    <div
      ref={containerRef}
      className="h-full w-full"
      style={{
        maxWidth:
          viewport === "mobile" && maxMobileWidth
            ? `${maxMobileWidth}px`
            : undefined,
      }}
    >
      {viewport === "mobile" ? (
        <MobilePreview className={mobileClassName} cropToFit={cropToFit}>
          {previewContent}
        </MobilePreview>
      ) : (
        <DesktopPreview
          url={url}
          variant="dark"
          className={twMerge(
            "h-full w-full max-w-full max-h-full overflow-hidden",
            desktopClassName,
          )}
          frameScale={desktopFrameScale}
        >
          {desktopPreviewContent}
        </DesktopPreview>
      )}
    </div>
  );
};
